<template>
  <div class="pdf-preview-wrapper" v-if="section">
    <SectionHeader :section="section"></SectionHeader>
    <div class="sub-section"
         v-for="subSection in visibleSubSections"
         :key="subSection.id"
         :id="subSection.id">
      <SubSectionHeader :section="section"
                        :sub-section="subSection"></SubSectionHeader>
      <SubSectionCategoryHeader :sub-section="subSection"
                                :item-categories="getItemCategories(subSection)"></SubSectionCategoryHeader>
      <div class="sub-sub-section"
           v-for="subSubSection in visibleSubSubSections(subSection)"
           :key="subSubSection.id"
           :id="subSubSection.id">
        <SubSubSectionHeader :section="section"
                             :sub-section="subSection"
                             :sub-sub-section="subSubSection"
                             :show-categories="true"
                             :item-categories="getItemCategories(subSection)"></SubSubSectionHeader>

        <ItemGroup v-for="itemGroup in visibleItemGroups(subSubSection)"
                   :key="itemGroup.id"
                   :section="section"
                   :sub-section="subSection"
                   :item-categories="getItemCategories(subSection)"
                   :item-group="itemGroup"
                   :items="getItems(itemGroup)"></ItemGroup>

        <ItemRemarkGroup v-for="itemRemarkGroup in getItemRemarkGroups(subSubSection)"
                         :key="itemRemarkGroup.id"
                         :section="section"
                         :sub-section="subSection"
                         :item-categories="getItemCategories(subSection)"
                         :item-remark-group="itemRemarkGroup"
                         :item-remarks="getItemRemarks(itemRemarkGroup)"></ItemRemarkGroup>
      </div>

      <SubSectionFooter :sub-section="subSection"
                        :item-categories="getItemCategories(subSection)"
                        :sub-sub-sections="visibleSubSubSections(subSection)"></SubSectionFooter>
    </div>
    <div class="footer-row">
      <div class="item-no left"></div>
      <div class="sub-section-name"></div>
      <div class="item-no right"></div>
    </div>
    <div class="footer-page-no">
      {{ section.order_no }}/XX
    </div>
  </div>
</template>

<script>
import {ACTION_TYPES} from "@/store/types";
import {computed, onMounted, ref} from "@vue/composition-api";
import SectionHeader from "@/components/pdf/SectionHeader";
import SubSectionHeader from "@/components/pdf/SubSectionHeader";
import SubSubSectionHeader from "@/components/pdf/SubSubSectionHeader";
import SubSectionCategoryHeader from "@/components/pdf/SubSectionTableHeader";
import ItemGroup from "@/components/pdf/ItemGroup";
import SubSectionFooter from "@/components/pdf/SubSectionFooter";
import ItemRemarkGroup from "@/components/pdf/ItemRemarkGroup";
import _ from "lodash";

export default {
  name: 'PdfPreview',
  components: {
    ItemRemarkGroup,
    SubSectionFooter,
    ItemGroup,
    SubSectionCategoryHeader,
    SubSubSectionHeader,
    SubSectionHeader,
    SectionHeader
  },
  props: {
    sectionId: String,
  },
  setup(props, {root}) {
    const section = ref(null);
    const subSections = ref([]);
    const visibleSubSections = computed(() => {
      return _.filter(subSections.value, (s) => !s.is_hidden);
    })
    const itemCategories = ref([]);
    const getItemCategories = function (subSection) {
      return _.filter(itemCategories.value, {'sub_section': subSection.id});
    }

    const subSubSections = ref([]);
    const visibleSubSubSections = function (subSection) {
      return _.filter(subSubSections.value, (s) => s.sub_section === subSection.id && !s.is_hidden);
    }

    const itemGroups = ref([])
    const visibleItemGroups = function (subSubSection) {
      return _.filter(itemGroups.value, (s) => s.sub_sub_section === subSubSection.id && !s.is_hidden);
    }

    const items = ref([]);
    const getItems = function (itemGroup) {
      return _.filter(items.value, {'item_group': itemGroup.id});
    }

    const itemRemarkGroups = ref([])
    const getItemRemarkGroups = function (subSubSection) {
      return _.filter(itemRemarkGroups.value, (s) => s.sub_sub_section === subSubSection.id);
    }

    const itemRemarks = ref([]);
    const getItemRemarks = function (itemRemarkGroup) {
      return _.filter(itemRemarks.value, (s) => s.item_remark_group === itemRemarkGroup.id);
    }

    const getSections = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: 'admin/section/',
        params: {
          'section_id': props.sectionId,
        },
      });

      section.value = response.body.section;
      subSections.value = response.body.sub_sections;
      itemCategories.value = response.body.item_categories;
      subSubSections.value = response.body.sub_sub_sections;
      itemGroups.value = response.body.item_groups;
      items.value = response.body.items;
      itemRemarkGroups.value = response.body.item_remark_groups;
      itemRemarks.value = response.body.item_remarks;
    }

    onMounted(() => {
      getSections();
    })

    return {
      section,
      visibleSubSections,
      getItemCategories,
      visibleSubSubSections,
      visibleItemGroups,
      getItems,
      getItemRemarkGroups,
      getItemRemarks,
    }
  }
}
</script>

<style lang="less">
.collect-btn {
  position: absolute;
  top: 24px;
  right: 24px;
}

.pdf-preview-wrapper {
  width: 620px !important;
  //height: 813px !important;

  display: flex;
  flex-direction: column;
  overflow: auto;

  font-family: 'Times New Roman' !important;
  font-size: 8px;

  line-height: normal !important;

  box-sizing: border-box;

  .section-header {
    display: flex;
    font-weight: bold;
    font-size: 150%;
  }

  .section-no {
    width: 72px;
    flex: 0 0 auto;
    padding: 16px 0;
    border-bottom: solid black 2px;

    display: flex;
    justify-content: center;
  }

  .section-name {
    flex: 1 1 0;
    padding: 16px 24px;
    border-left: solid black 1px;
    border-right: solid black 1px;
    border-bottom: solid black 2px;
    text-transform: uppercase;
  }

  .item-no-header {
    display: flex;
    justify-content: space-between;
  }

  .item-no-header > .item-no {
    padding: 8px 0;
    border-bottom: solid 1px black;
  }

  .item-no {
    width: 36px;
    display: flex;
    justify-content: center;
    padding-top: 8px;
  }

  .item-no.left {
    border-right: solid 1px black;
    align-items: flex-start;
  }

  .item-no.right {
    border-left: solid 1px black;
    align-items: flex-end;
  }

  .item-no.flat {
    padding-top: 0 !important;
  }

  .content-row {
    display: flex;
  }

  .sub-section-name {
    flex: 1 1 0;
    padding: 14px 4px 0;
    text-transform: uppercase;
    font-weight: bold;
  }

  .sub-section-descriptions {
    flex: 1 1 0;
    padding: 14px 4px 4px;
  }

  .sub-section-descriptions table {
    font-size: 100%;
    border-collapse: collapse;
  }

  .sub-section-descriptions td {
    border: solid 1px black;
    width: 34px;
    padding: 1px;
  }

  .category-header-wrapper {
    flex: 0 0 auto;
  }

  .category-header-wrapper table {
    font-size: 100%;
    border-collapse: collapse;
    position: relative;
    bottom: -1px;
    right: -1px;
  }

  .category-header-wrapper td {
    border: solid 1px black;
    width: 35px;
    padding: 1px;
    text-align: center;
  }

  .sub-sub-section-header {
    flex: 1 1 0;
    padding-left: 4px;
    padding-top: 10px;
  }

  .sub-sub-section-descriptions {
    padding: 20px 12px 0 0;
  }

  .sub-sub-section-descriptions table {
    font-size: 100%;
    border-collapse: collapse;
  }

  .sub-sub-section-descriptions td {
    border: solid 1px black;
  }

  .sub-sub-section-unit {
    flex: 1 1 0;
    padding-top: 10px;
    font-weight: bold;
  }

  .sub-sub-section-unit.flat {
    padding-top: 0 !important;
  }

  .category {
    width: 39px;
    border-top: solid 1px black;
    border-left: solid 1px black;
    text-align: center;
    position: relative;
  }

  .category :nth-child(2) {
    height: 100%;
  }

  .item-description-wrapper {
    flex: 1 1 0;
    padding-top: 8px;
    padding-right: 12px;
    margin-left: 3px;
    overflow: hidden;
  }

  .item-description-wrapper.remarks {
    border-bottom: none !important;
    padding-top: 0 !important;
    margin-left: 4px !important;
  }

  .item-description-wrapper table {
    font-size: 100%;
    border-collapse: collapse;
    position: relative;
    top: -4px;
  }

  .item-description-wrapper td {
    border: solid 1px black;
    width: 35px;
    padding: 1px;
    text-align: center;
  }

  .item-description {
    background: white;
    position: relative;
    left: 1px;
  }

  .item-description:after {
    content: "............................................................................................................................................................................................................................................................................................................................................................................................................................................................................................";
    position: absolute;
    padding-left: 2px;
  }

  .item-price {
    width: 39px;
    border-left: solid 1px black;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    font-size: 100%;
    padding-right: 2px;
  }

  .item-price.small {
    font-size: 90%;
  }

  .item-price-placeholder {
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    font-size: 80%;
  }

  .item-price.bottom {
    border-bottom: solid 1px black;
  }

  .item-group-footer {
    flex: 1 1 0;
    padding-top: 0;
  }

  .footer-row {
    flex: 1 1 0;
    display: flex;
  }

  .footer-page-no {
    text-align: center;
  }
}
</style>
